import { activity_logs, VITE_API_URL } from "@/constants";
import type { ActivityLog } from "@/lib/types";
import { isTokenInvalid } from "@/lib/api-client";
import { queryClient } from "@/main";
import { useAuth } from "@/providers/auth";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import type { AuthFetchParams } from "./global";
import { useOrg } from "@/providers/organization";

export async function queryActivity(params: AuthFetchParams) {
  const res = await fetch(`${VITE_API_URL}${activity_logs}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  return (await res.json()) as ActivityLog[];
}

export const activityQueryOpts = (params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["activities"],
    queryFn: async () => await queryActivity(params),
    refetchInterval: 15_000,
  });

export const useDeleteActivityMutation = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${VITE_API_URL}${activity_logs}/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
      });

      // Check if token is invalid and handle logout
      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const token = res.headers.get("access-token");
      if (token) setToken(token);

      return await res.json();
    },
    onSuccess: () => {
      toast.success(i18n.t("toast.activityDeleted"));
    },
  });
};

export const useDeleteActivitiesMutation = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch(`${VITE_API_URL}${activity_logs}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
        body: JSON.stringify({
          ids: ids,
        }),
      });

      // Check if token is invalid and handle logout
      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const token = res.headers.get("access-token");
      if (token) setToken(token);

      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast.success(i18n.t("toast.activityDeleted"));
    },
  });
};
